export interface ValidationRule {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  email?: boolean;
  password?: boolean;
  confirmPassword?: string; // field name to match
  custom?: (value: any) => string | null;
}

export interface ValidationError {
  field: string;
  message: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

export class FormValidator {
  private rules: Record<string, ValidationRule> = {};
  private errors: ValidationError[] = [];

  constructor(rules: Record<string, ValidationRule>) {
    this.rules = rules;
  }

  validate(data: Record<string, any>): ValidationResult {
    this.errors = [];

    Object.keys(this.rules).forEach(field => {
      const rule = this.rules[field];
      const value = data[field];

      this.validateField(field, value, rule, data);
    });

    return {
      isValid: this.errors.length === 0,
      errors: this.errors,
    };
  }

  validateField(
    field: string,
    value: any,
    rule: ValidationRule,
    allData?: Record<string, any>
  ): string | null {
    // Required validation
    if (
      rule.required &&
      (!value || (typeof value === 'string' && value.trim() === ''))
    ) {
      const error = `${this.formatFieldName(field)} is required`;
      this.addError(field, error);
      return error;
    }

    // Skip other validations if field is empty and not required
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      return null;
    }

    // String validations
    if (typeof value === 'string') {
      // Min length
      if (rule.minLength && value.length < rule.minLength) {
        const error = `${this.formatFieldName(field)} must be at least ${rule.minLength} characters`;
        this.addError(field, error);
        return error;
      }

      // Max length
      if (rule.maxLength && value.length > rule.maxLength) {
        const error = `${this.formatFieldName(field)} must be no more than ${rule.maxLength} characters`;
        this.addError(field, error);
        return error;
      }

      // Email validation
      if (rule.email && !this.isValidEmail(value)) {
        const error = 'Please enter a valid email address';
        this.addError(field, error);
        return error;
      }

      // Password validation
      if (rule.password && !this.isValidPassword(value)) {
        const error =
          'Password must be at least 8 characters with uppercase, lowercase, number, and special character';
        this.addError(field, error);
        return error;
      }

      // Pattern validation
      if (rule.pattern && !rule.pattern.test(value)) {
        const error = `${this.formatFieldName(field)} format is invalid`;
        this.addError(field, error);
        return error;
      }
    }

    // Confirm password validation
    if (rule.confirmPassword && allData) {
      const passwordValue = allData[rule.confirmPassword];
      if (value !== passwordValue) {
        const error = 'Passwords do not match';
        this.addError(field, error);
        return error;
      }
    }

    // Custom validation
    if (rule.custom) {
      const customError = rule.custom(value);
      if (customError) {
        this.addError(field, customError);
        return customError;
      }
    }

    return null;
  }

  private addError(field: string, message: string) {
    // Avoid duplicate errors for the same field
    if (
      !this.errors.find(
        error => error.field === field && error.message === message
      )
    ) {
      this.errors.push({ field, message });
    }
  }

  private formatFieldName(field: string): string {
    return field
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  private isValidPassword(password: string): boolean {
    // At least 8 characters, 1 uppercase, 1 lowercase, 1 number, 1 special character
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return passwordRegex.test(password);
  }
}

// Utility functions for common validations
export const validateEmail = (email: string): string | null => {
  const validator = new FormValidator({
    email: { email: true, required: true },
  });
  const result = validator.validate({ email });
  return result.errors.length > 0 ? result.errors[0].message : null;
};

export const validatePassword = (password: string): string | null => {
  const validator = new FormValidator({
    password: { password: true, required: true },
  });
  const result = validator.validate({ password });
  return result.errors.length > 0 ? result.errors[0].message : null;
};

export const validateRequired = (
  value: any,
  fieldName: string
): string | null => {
  if (!value || (typeof value === 'string' && value.trim() === '')) {
    return `${fieldName} is required`;
  }
  return null;
};

// Real-time validation hook
import { useState, useCallback } from 'react';

export interface UseFormValidationOptions {
  rules: Record<string, ValidationRule>;
  validateOnChange?: boolean;
  validateOnBlur?: boolean;
}

export const useFormValidation = (options: UseFormValidationOptions) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const validator = new FormValidator(options.rules);

  const validateField = useCallback(
    (field: string, value: any, allData?: Record<string, any>) => {
      const rule = options.rules[field];
      if (!rule) return null;

      const error = validator.validateField(field, value, rule, allData);

      setErrors(prev => ({
        ...prev,
        [field]: error || '',
      }));

      return error;
    },
    [options.rules, validator]
  );

  const validateForm = useCallback(
    (data: Record<string, any>) => {
      const result = validator.validate(data);

      const errorMap: Record<string, string> = {};
      result.errors.forEach(error => {
        errorMap[error.field] = error.message;
      });

      setErrors(errorMap);
      return result;
    },
    [validator]
  );

  const handleFieldChange = useCallback(
    (field: string, value: any, allData?: Record<string, any>) => {
      if (options.validateOnChange) {
        validateField(field, value, allData);
      }
    },
    [validateField, options.validateOnChange]
  );

  const handleFieldBlur = useCallback(
    (field: string, value: any, allData?: Record<string, any>) => {
      setTouched(prev => ({ ...prev, [field]: true }));

      if (options.validateOnBlur) {
        validateField(field, value, allData);
      }
    },
    [validateField, options.validateOnBlur]
  );

  const clearErrors = useCallback(() => {
    setErrors({});
    setTouched({});
  }, []);

  const clearFieldError = useCallback((field: string) => {
    setErrors(prev => ({ ...prev, [field]: '' }));
  }, []);

  return {
    errors,
    touched,
    validateField,
    validateForm,
    handleFieldChange,
    handleFieldBlur,
    clearErrors,
    clearFieldError,
    hasErrors: Object.values(errors).some(error => error !== ''),
    getFieldError: (field: string) => (touched[field] ? errors[field] : ''),
  };
};
